import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Mail, Calendar, User, AlertCircle, CheckCircle, Clock, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/submissions`);
      if (response.data.success) {
        setSubmissions(response.data.submissions);
      }
    } catch (err) {
      setError('Failed to load submissions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/submissions/${id}?status=${newStatus}`);
      fetchSubmissions();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'text-[#D4A853] border-[#D4A853]';
      case 'reviewed': return 'text-[#8B7355] border-[#8B7355]';
      case 'approved': return 'text-[#6B7B4B] border-[#6B7B4B]';
      case 'rejected': return 'text-[#8B0000] border-[#8B0000]';
      case 'contacted': return 'text-[#4B7B8B] border-[#4B7B8B]';
      default: return 'text-[#8B7355] border-[#8B7355]';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <AlertCircle className="w-4 h-4" />;
      case 'reviewed': return <Clock className="w-4 h-4" />;
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <X className="w-4 h-4" />;
      case 'contacted': return <Mail className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0908] flex items-center justify-center">
        <div className="text-[#D4A853] text-xl tracking-widest">Loading submissions...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0908] py-8 px-4 md:px-6">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-[#D4A853] text-3xl md:text-4xl font-serif tracking-wider mb-2">
            Session Applications
          </h1>
          <p className="text-[#8B7355] tracking-wide">
            {submissions.length} submission{submissions.length !== 1 ? 's' : ''} received
          </p>
        </div>

        {error && (
          <div className="mb-8 p-4 border border-[#8B0000] text-[#8B0000] bg-[#8B0000]/10 text-center">
            {error}
          </div>
        )}

        {/* Submissions List */}
        <div className="space-y-4">
          {submissions.length === 0 ? (
            <div className="text-center text-[#8B7355] py-12">
              No submissions yet.
            </div>
          ) : (
            submissions.map((submission) => (
              <div
                key={submission.id}
                className="border border-[#8B7355]/30 bg-[#0d0b09]"
              >
                {/* Summary Row */}
                <div
                  className="p-4 md:p-6 cursor-pointer hover:bg-[#D4A853]/5 transition-colors"
                  onClick={() => setExpandedId(expandedId === submission.id ? null : submission.id)}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <User className="w-5 h-5 text-[#D4A853]" />
                      <div>
                        <h3 className="text-[#A69080] text-lg">{submission.name}</h3>
                        <p className="text-[#8B7355] text-sm">{submission.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 text-[#8B7355] text-sm">
                        <Calendar className="w-4 h-4" />
                        {formatDate(submission.submitted_at)}
                      </div>
                      
                      <div className={`flex items-center gap-1 px-3 py-1 border text-xs uppercase tracking-wider ${getStatusColor(submission.status)}`}>
                        {getStatusIcon(submission.status)}
                        {submission.status}
                      </div>
                      
                      {expandedId === submission.id ? (
                        <ChevronUp className="w-5 h-5 text-[#8B7355]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#8B7355]" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedId === submission.id && (
                  <div className="border-t border-[#8B7355]/30 p-4 md:p-6 space-y-6">
                    {/* Quick Actions */}
                    <div className="flex flex-wrap gap-2">
                      <span className="text-[#8B7355] text-sm mr-2">Update Status:</span>
                      {['new', 'reviewed', 'contacted', 'approved', 'rejected'].map((status) => (
                        <button
                          key={status}
                          onClick={(e) => { e.stopPropagation(); updateStatus(submission.id, status); }}
                          className={`px-3 py-1 border text-xs uppercase tracking-wider transition-colors hover:bg-opacity-20 ${
                            submission.status === status
                              ? `${getStatusColor(status)} bg-current bg-opacity-10`
                              : 'border-[#8B7355]/30 text-[#8B7355] hover:border-[#D4A853]'
                          }`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>

                    {/* Details Grid */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-[#D4A853] text-sm uppercase tracking-wider mb-2">Personal Info</h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-[#8B7355]">Age:</span> <span className="text-[#A69080]">{submission.age}</span></p>
                          <p><span className="text-[#8B7355]">Height/Weight:</span> <span className="text-[#A69080]">{submission.height_weight || 'Not provided'}</span></p>
                          <p><span className="text-[#8B7355]">Experience:</span> <span className="text-[#A69080]">{submission.experience_level}</span></p>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-[#D4A853] text-sm uppercase tracking-wider mb-2">Session Request</h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-[#8B7355]">Preferred Time:</span> <span className="text-[#A69080]">{submission.preferred_date_time}</span></p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-[#D4A853] text-sm uppercase tracking-wider mb-2">Interests & Fetishes</h4>
                      <p className="text-[#A69080] text-sm whitespace-pre-wrap">{submission.interests}</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-[#8B0000] text-sm uppercase tracking-wider mb-2">Hard Limits</h4>
                        <p className="text-[#A69080] text-sm whitespace-pre-wrap">{submission.hard_limits}</p>
                      </div>
                      <div>
                        <h4 className="text-[#6B7B4B] text-sm uppercase tracking-wider mb-2">Soft Limits</h4>
                        <p className="text-[#A69080] text-sm whitespace-pre-wrap">{submission.soft_limits || 'None specified'}</p>
                      </div>
                    </div>

                    {(submission.medical_conditions || submission.triggers_phobias) && (
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-[#D4A853] text-sm uppercase tracking-wider mb-2">Medical Conditions</h4>
                          <p className="text-[#A69080] text-sm whitespace-pre-wrap">{submission.medical_conditions || 'None listed'}</p>
                        </div>
                        <div>
                          <h4 className="text-[#D4A853] text-sm uppercase tracking-wider mb-2">Triggers & Phobias</h4>
                          <p className="text-[#A69080] text-sm whitespace-pre-wrap">{submission.triggers_phobias || 'None listed'}</p>
                        </div>
                      </div>
                    )}

                    {submission.references && (
                      <div>
                        <h4 className="text-[#D4A853] text-sm uppercase tracking-wider mb-2">References</h4>
                        <p className="text-[#A69080] text-sm whitespace-pre-wrap">{submission.references}</p>
                      </div>
                    )}

                    {/* Email Link */}
                    <div className="pt-4 border-t border-[#8B7355]/30">
                      <a
                        href={`mailto:${submission.email}?subject=Re: Session Application&body=Hello ${submission.name},%0D%0A%0D%0AThank you for your session application...`}
                        className="inline-flex items-center gap-2 px-6 py-2 border border-[#D4A853] text-[#D4A853] text-sm uppercase tracking-wider hover:bg-[#D4A853] hover:text-[#0a0908] transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                        Reply to {submission.name}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSubmissions;
